import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import * as Sentry from '@sentry/react';
import BEM from '../../bem';
import getApiErrorMessage from '../../utils/getApiErrorMessage';
import mobxInjectSelect from '../../utils/mobxInjectSelect';
import validatePbxPhone from '../../utils/validatePbxPhone';
import TCClient from '../../../client';
import { PBXLineDetails } from '../../../types';
import Modal from '../Modal';

const classes = BEM.with('RoleLineDetailsModal');

type RoleLineDetailsModalProps = {
  options: {
    pbxLineId: string;
    onSuccess?: () => void;
  };
};

type MobxProps = {
  closeModal: () => void;
  currentOrganizationId: string;
  isOpen: boolean;
};

type FormFields = {
  callerIdNumber: string;
  emergencyCallerIdNumber: string;
  forwardAlwaysEnabled: boolean;
  forwardAlwaysDestination: string;
};

const DEFAULT_VALUES: FormFields = {
  callerIdNumber: '',
  emergencyCallerIdNumber: '',
  forwardAlwaysEnabled: false,
  forwardAlwaysDestination: '',
};

const RoleLineDetailsModal = ({
  closeModal,
  currentOrganizationId,
  isOpen,
  options: { pbxLineId, onSuccess },
}: MobxProps & RoleLineDetailsModalProps) => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<FormFields>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onSubmit',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [lineDetails, setLineDetails] = useState<PBXLineDetails | null>(null);

  const extension = lineDetails?.customSettings?.extensionNumber || '';
  const displayName = lineDetails?.accountSettings?.displayName || '';

  useEffect(() => {
    if (!isOpen || !pbxLineId) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setLoadError('');
    setApiError('');
    reset(DEFAULT_VALUES);
    setLineDetails(null);

    (async () => {
      try {
        const details = (await TCClient.pbx.getPbxLineDetails({
          orgId: currentOrganizationId,
          pbxLineId,
          detailed: true,
        })) as PBXLineDetails;
        if (cancelled) return;
        setLineDetails(details);
        reset({
          callerIdNumber: details.callerIdNumber || DEFAULT_VALUES.callerIdNumber,
          emergencyCallerIdNumber:
            details.emergencyCallerIdNumber || DEFAULT_VALUES.emergencyCallerIdNumber,
          forwardAlwaysEnabled: details.forwardAlwaysEnabled || DEFAULT_VALUES.forwardAlwaysEnabled,
          forwardAlwaysDestination:
            details.forwardAlwaysDestination || DEFAULT_VALUES.forwardAlwaysDestination,
        });
      } catch (err) {
        if (!cancelled) {
          setLoadError('Failed to load line details. Please try again.');
          Sentry.logger.error(Sentry.logger.fmt`Failed to load PBX role line details: ${err}`);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, pbxLineId, currentOrganizationId, reset]);

  const onSubmit = async (formData: FormFields) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setApiError('');

    try {
      await TCClient.pbx.updatePbxLineDetails({
        orgId: currentOrganizationId,
        pbxLineId,
        details: {
          callerIdNumber: formData.callerIdNumber,
          emergencyCallerIdNumber: formData.emergencyCallerIdNumber,
        },
      });
      onSuccess?.();
      closeModal();
    } catch (err) {
      setApiError(getApiErrorMessage(err, 'Failed to save line details. Please try again.'));
      Sentry.logger.error(Sentry.logger.fmt`Failed to save PBX role line details: ${err}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBusy = isLoading || isSubmitting;

  return (
    <Modal
      bodyClass={classes('body')}
      closeClass={classes('close-btn')}
      footerClass={classes('footer')}
      header="Edit Role Line Details"
      headerClass={classes('header')}
      isOpen={isOpen}
      size="large"
      className={classes()}
      onRequestClose={closeModal}
      footerPrimaryActions={
        <button
          aria-label="Save"
          className={classes('primary-btn')}
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={isBusy || Boolean(loadError)}
        >
          {isSubmitting ? 'Saving...' : 'Save Line'}
        </button>
      }
      footerSecondaryActions={
        <button
          aria-label="Cancel"
          className={classes('secondary-btn')}
          type="button"
          onClick={closeModal}
        >
          Cancel
        </button>
      }
    >
      {isLoading && <div className={classes('status')}>Loading...</div>}
      {!isLoading && loadError && (
        <div className={classes('status', { error: true })}>{loadError}</div>
      )}
      {!isLoading && !loadError && (
        <>
          <div className={classes('line-header')}>
            <div className={classes('line-header-text')}>
              <div className={classes('line-header-name')}>{displayName || 'Unassigned'}</div>
              <div className={classes('line-header-extension')}>Extension {extension}</div>
            </div>
          </div>

          <div className={classes('note')}>
            Caller ID Number and Emergency Caller ID are required for all lines.
          </div>

          <div className={classes('form-container')}>
            <div className={classes('form-row')}>
              <div className={classes('form-field')}>
                <label className={classes('label')}>
                  Caller ID Number
                  <span className={classes('required')}>*</span>
                </label>
                <Controller
                  name="callerIdNumber"
                  control={control}
                  rules={{ validate: validatePbxPhone }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <input
                      className={classes('input', { error: Boolean(errors.callerIdNumber) })}
                      type="text"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      placeholder="Enter caller ID number"
                    />
                  )}
                />
                {errors.callerIdNumber && (
                  <span className={classes('error-message')}>{errors.callerIdNumber.message}</span>
                )}
              </div>

              <div className={classes('form-field')}>
                <label className={classes('label')}>
                  Emergency Caller ID
                  <span className={classes('required')}>*</span>
                </label>
                <Controller
                  name="emergencyCallerIdNumber"
                  control={control}
                  rules={{ validate: validatePbxPhone }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <input
                      className={classes('input', {
                        error: Boolean(errors.emergencyCallerIdNumber),
                      })}
                      type="text"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      placeholder="Enter emergency caller ID"
                    />
                  )}
                />
                {errors.emergencyCallerIdNumber && (
                  <span className={classes('error-message')}>
                    {errors.emergencyCallerIdNumber.message}
                  </span>
                )}
              </div>
            </div>

            <div className={classes('form-row')}>
              <div className={classes('form-field')}>
                <label className={classes('label')}>Forward Always Enabled</label>
                <Controller
                  name="forwardAlwaysEnabled"
                  control={control}
                  render={({ field: { value } }) => (
                    <select className={classes('select')} value={value ? 'Yes' : 'No'} disabled>
                      <option>No</option>
                      <option>Yes</option>
                    </select>
                  )}
                />
              </div>
              <div className={classes('form-field')}>
                <label className={classes('label')}>Forward Always Destination</label>
                <Controller
                  name="forwardAlwaysDestination"
                  control={control}
                  render={({ field: { value } }) => (
                    <input
                      className={classes('input')}
                      type="text"
                      value={value}
                      disabled
                      placeholder="—"
                    />
                  )}
                />
              </div>
            </div>

            {apiError && <div className={classes('error-message')}>{apiError}</div>}
          </div>
        </>
      )}
    </Modal>
  );
};

export default mobxInjectSelect<RoleLineDetailsModalProps, MobxProps>({
  messengerStore: ['currentOrganizationId'],
})(RoleLineDetailsModal);
